import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addNotification } from '../../store/slices/notificationSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import robotIllustration from '../../assets/img/Illustration-Login.png';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { useTranslation } from 'react-i18next';
import LoginLayout from '../../components/layout/LoginLayout';
import { AuthenticationService } from '../../api/generated/services/AuthenticationService';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setVerificationStatus('error');
        setErrorMessage(t('verify_email_invalid_token'));
        setIsVerifying(false);
        return;
      }

      try {
        await AuthenticationService.getAppAuthVerifyEmail(token);
        setVerificationStatus('success');
        
        dispatch(addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('verify_email_success'),
        }));

        // Redirect after short delay
        setTimeout(() => {
          if (isAuthenticated) {
            navigate('/dashboard');
          } else {
            navigate('/login');
          }
        }, 2000);
      } catch (err) {
        setVerificationStatus('error');
        
        let message = t('verify_email_error');
        if (err instanceof ApiError) {
          const parsedBody = helper.parseApiErrorBody(err);
          if (parsedBody && parsedBody.error) {
            message = parsedBody.error;
          }
        }
        setErrorMessage(message);
        
        dispatch(addNotification({
          type: 'error',
          title: t('notification_error'),
          message: message,
        }));
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [token, dispatch, navigate, t, isAuthenticated]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <LoginLayout>
      <div className="login-illustration">
        <img src={robotIllustration} alt={t('verify_email_title')} />
      </div>

      <Header isLoginHeader={true} showLoginHeader={true} />

      <div className="login-content">
        <div className="login-form-container">
          <div className="login-titles">
            <h1>{t('verify_email_title')}</h1>
            
            {isVerifying && (
              <p>{t('verify_email_verifying')}</p>
            )}
            
            {verificationStatus === 'success' && (
              <>
                <p className="success-text">{t('verify_email_success')}</p>
                <p>{t('verify_email_redirecting')}</p>
              </>
            )}
            
            {verificationStatus === 'error' && (
              <>
                <p className="error-text">{errorMessage}</p>
              </>
            )}
          </div>

          {!isVerifying && (
            <div className="verify-email-actions">
              {isAuthenticated ? (
                <button
                  type="button"
                  className="login-button"
                  onClick={handleGoToDashboard}
                >
                  {t('verify_email_go_to_dashboard')}
                </button>
              ) : (
                <button
                  type="button"
                  className="login-button"
                  onClick={handleGoToLogin}
                >
                  {t('verify_email_go_to_login')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </LoginLayout>
  );
};

export default VerifyEmailPage;
